// Parity P5 wave 1 (hybrid design §6.1 automations, §6.2 recurring work). Automations: the
// Work › Automations collection (company-wide rules and each board's), the builder's CRUD with
// its switch, duplicate and order, the firing History, and "Describe it" (a sentence drafted into
// a rule the person then saves). A person's write applies; an agent's (`create_automation`,
// `delete_automation`, `propose_routine`) is filed as an `automation` approval (§8.1
// `automation_write`: Ask) and applies as the approving person, who becomes the rule's owner.
// Recurring work: Chart › seat's routines with pause / resume / skip next / take over and the
// history, the drawer's Repeat… dialog, and a seat's new routine.
import { z } from "zod";
import { AutomationScope } from "../enums";
import { LIMITS, text, Uuid } from "../primitives";
import {
  Automation,
  AutomationAction,
  AutomationCondition,
  AutomationRun,
  AutomationSpec,
  AutomationTrigger,
  RecurringControl,
  RecurringRun,
} from "../process-rows";
import { RecurringWork, TaskTemplate } from "../rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** A rule as the collection shows it: the owner's and the board's names, the agent who asked. */
export const AutomationView = Automation.extend({
  owner_name: z.string().nullable(),
  board_name: z.string().nullable(),
  proposed_by_name: z.string().nullable(),
  /** B1-DE-RULES: the DE holding the owner seat of a DE's rule (null: a person's rule). */
  owner_de_name: z.string().nullable(),
  /** The rule as one sentence ("When status becomes Hired, then create task …"). */
  sentence: z.string(),
  /** Whether the caller may edit, switch or delete it (else read only). */
  can_edit: z.boolean(),
}).meta({ id: "AutomationView" });
export type AutomationView = z.infer<typeof AutomationView>;

/** An agent's pending proposal (the "◌ 🤖 proposes … Review →" row). */
export const AutomationProposalView = z
  .object({
    approval_id: Uuid,
    op: z.enum(["create", "update", "toggle", "delete"]),
    automation_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
    requested_by_seat_id: Uuid.nullable(),
    requested_by_name: z.string().nullable(),
    /** What it would do, in one sentence. */
    sentence: z.string(),
    created_at: z.string(),
  })
  .meta({ id: "AutomationProposalView" });
export type AutomationProposalView = z.infer<typeof AutomationProposalView>;

export const AutomationList = z
  .object({ data: z.array(AutomationView), proposals: z.array(AutomationProposalView) })
  .meta({ id: "AutomationList" });

export const AutomationListQuery = z.object({
  /** Only this board's rules (and company-wide ones with `include_company=true`). */
  board_id: Uuid.optional(),
  scope: AutomationScope.optional(),
});

/** `POST automations`: the rule, and whether it starts switched on (default on). */
export const AutomationCreate = AutomationSpec.extend({
  enabled: z.boolean().optional(),
}).meta({ id: "AutomationCreate" });

/** `PATCH automations/{id}` (If-Match: the rule's version). */
export const AutomationPatch = z
  .strictObject({
    name: text(LIMITS.name).optional(),
    trigger: AutomationTrigger.optional(),
    conditions: z.array(AutomationCondition).max(10).optional(),
    actions: z.array(AutomationAction).min(1).max(10).optional(),
    effect_budget_per_hour: z.int().min(1).max(10_000).optional(),
    /** The ⋮⋮ order in the collection (lower first). */
    position: z.int().min(0).optional(),
  })
  .meta({ id: "AutomationPatch" });

/**
 * A write's outcome: the rule as it now is (null after a delete), or, for an agent, the approval
 * its proposal was filed as (nothing changed yet).
 */
export const AutomationResult = z
  .object({
    automation: AutomationView.nullable(),
    proposed: z.boolean(),
    approval_id: Uuid.nullable(),
  })
  .meta({ id: "AutomationResult" });
export type AutomationResult = z.infer<typeof AutomationResult>;

/** A firing as the History lists it: whom it acted as, by name (a DE is labelled DE). */
export const AutomationRunView = AutomationRun.extend({
  acted_as_name: z.string().nullable(),
  acted_as_de: z.boolean(),
}).meta({ id: "AutomationRunView" });

export const AutomationRunList = z
  .object({ data: z.array(AutomationRunView) })
  .meta({ id: "AutomationRunList" });

export const AutomationRunsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const AutomationDraftRequest = z
  .strictObject({
    text: text(LIMITS.text),
    /** The board the builder is open on (the draft's scope and its statuses). */
    board_id: Uuid.optional(),
  })
  .meta({ id: "AutomationDraftRequest" });

/**
 * "Describe it": the parts of the sentence that were understood, filled into the builder's
 * shape (any part may be missing: the person completes it), and what was not understood.
 */
export const AutomationDraft = z
  .object({
    name: z.string(),
    trigger: AutomationTrigger.nullable(),
    conditions: z.array(AutomationCondition),
    actions: z.array(AutomationAction),
    understood: z.array(z.string()),
    unmatched: z.array(z.string()),
  })
  .meta({ id: "AutomationDraft" });
export type AutomationDraft = z.infer<typeof AutomationDraft>;

/**
 * B1-DE-RULES: the plain-language snippets the drafter offers (the Work OS recipe gallery as
 * sentences), filled with the board's own names; each drafts into a whole rule.
 */
export const AutomationRecipes = z
  .object({ data: z.array(z.object({ id: z.string(), text: z.string() })) })
  .meta({ id: "AutomationRecipes" });

export const AutomationRecipesQuery = z.object({ board_id: Uuid.optional() });

// Recurring work -----------------------------------------------------------------------------------

/** One routine as Chart › seat lists it. */
export const RoutineView = z
  .object({
    recurring_work: RecurringWork,
    responsibility: z.object({ id: Uuid, name: z.string(), seat_id: Uuid }),
    control: RecurringControl.nullable(),
    board_name: z.string().nullable(),
    /** The seat that takes the work over, by name, when one does. */
    taken_over_by_name: z.string().nullable(),
    last_run: RecurringRun.nullable(),
  })
  .meta({ id: "RoutineView" });

export type RoutineView = z.infer<typeof RoutineView>;

export const RoutineList = z.object({ data: z.array(RoutineView) }).meta({ id: "RoutineList" });

/** `POST recurring-work/{id}/take-over`: this occurrence or all future ones, by a seat. */
export const RecurringTakeOver = z
  .strictObject({
    scope: z.enum(["occurrence", "all"]),
    /** The seat taking it over (default: the caller's first seat). The routine's own seat hands it back. */
    seat_id: Uuid.optional(),
  })
  .meta({ id: "RecurringTakeOver" });

export const RecurringHistory = z
  .object({ data: z.array(RecurringRun) })
  .meta({ id: "RecurringHistory" });

/**
 * A new routine on a seat (`POST seats/{id}/routines`; the drawer's Repeat… sends the same
 * through `tasks/{id}/repeat` with the task as its template): an existing responsibility of the
 * seat or a new one by name, and a cadence (RRULE in the company's time zone) or "after
 * completion".
 */
const routineBase = {
  responsibility_id: Uuid.optional(),
  responsibility_name: text(LIMITS.name).optional(),
  schedule: z.string().min(1).max(500).optional(),
  after_completion_days: z.int().min(1).max(365).optional(),
};
const oneResponsibility = (r: {
  responsibility_id?: string | undefined;
  responsibility_name?: string | undefined;
}) => (r.responsibility_id === undefined) !== (r.responsibility_name === undefined);
const oneCadence = (r: {
  schedule?: string | undefined;
  after_completion_days?: number | undefined;
}) => (r.schedule === undefined) !== (r.after_completion_days === undefined);

export const RoutineCreate = z
  .strictObject({ ...routineBase, board_id: Uuid, task_template: TaskTemplate })
  .refine(oneResponsibility, {
    message: "exactly one of responsibility_id and responsibility_name",
  })
  .refine(oneCadence, { message: "exactly one of schedule and after_completion_days" })
  .meta({ id: "RoutineCreate" });

export const TaskRepeat = z
  .strictObject({ ...routineBase, seat_id: Uuid })
  .refine(oneResponsibility, {
    message: "exactly one of responsibility_id and responsibility_name",
  })
  .refine(oneCadence, { message: "exactly one of schedule and after_completion_days" })
  .meta({ id: "TaskRepeat" });

/** A routine created, or (an agent) the approval its proposal was filed as. */
export const RoutineResult = z
  .object({
    routine: RoutineView.nullable(),
    proposed: z.boolean(),
    approval_id: Uuid.nullable(),
  })
  .meta({ id: "RoutineResult" });
export type RoutineResult = z.infer<typeof RoutineResult>;

const automationErrors = ["policy_denied", "conflict"] as const;

export const processRoutes = {
  "automations.list": defineRoute({
    ...core,
    id: "automations.list",
    method: "get",
    path: `${base}/automations`,
    tag: "automations",
    summary: "Automation rules the caller can see, and agents' pending proposals",
    access: "member",
    params: CompanyParams,
    query: AutomationListQuery,
    response: { status: 200, schema: AutomationList },
    errors: [],
    emits: [],
  }),
  "automations.create": defineRoute({
    ...core,
    id: "automations.create",
    method: "post",
    path: `${base}/automations`,
    tag: "automations",
    summary: "Create a rule (an agent's is filed as an automation proposal)",
    access: "member",
    params: CompanyParams,
    body: AutomationCreate,
    idempotency: "optional",
    response: { status: 201, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.created", "approval.requested"],
  }),
  "automations.draft": defineRoute({
    ...core,
    id: "automations.draft",
    method: "post",
    path: `${base}/automations/draft`,
    tag: "automations",
    summary: "Draft a rule from a sentence (Describe it); nothing is saved",
    access: "member",
    params: CompanyParams,
    body: AutomationDraftRequest,
    response: { status: 200, schema: AutomationDraft },
    errors: [],
    emits: [],
    safe: true,
  }),
  "automations.recipes": defineRoute({
    ...core,
    id: "automations.recipes",
    method: "get",
    path: `${base}/automations/recipes`,
    tag: "automations",
    summary: "Rule snippets in plain words, with the board's names (B1-DE-RULES)",
    access: "member",
    params: CompanyParams,
    query: AutomationRecipesQuery,
    response: { status: 200, schema: AutomationRecipes },
    errors: [],
    emits: [],
  }),
  "automations.get": defineRoute({
    ...core,
    id: "automations.get",
    method: "get",
    path: `${base}/automations/{id}`,
    tag: "automations",
    summary: "One rule",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AutomationView },
    errors: [],
    emits: [],
  }),
  "automations.update": defineRoute({
    ...core,
    id: "automations.update",
    method: "patch",
    path: `${base}/automations/{id}`,
    tag: "automations",
    summary: "Change a rule's definition, order or budget (an agent's is a proposal)",
    access: "member",
    params: CompanyItemParams,
    body: AutomationPatch,
    ifMatch: true,
    response: { status: 200, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.updated", "approval.requested"],
  }),
  "automations.delete": defineRoute({
    ...core,
    id: "automations.delete",
    method: "delete",
    path: `${base}/automations/{id}`,
    tag: "automations",
    summary: "Delete a rule and its history (an agent's is a proposal)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.deleted", "approval.requested"],
  }),
  "automations.enable": defineRoute({
    ...core,
    id: "automations.enable",
    method: "post",
    path: `${base}/automations/{id}/enable`,
    tag: "automations",
    summary: "Switch a rule on (a paused rule resumes, its owner's access re-checked)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.updated", "approval.requested"],
  }),
  "automations.disable": defineRoute({
    ...core,
    id: "automations.disable",
    method: "post",
    path: `${base}/automations/{id}/disable`,
    tag: "automations",
    summary: "Switch a rule off",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.updated", "approval.requested"],
  }),
  "automations.duplicate": defineRoute({
    ...core,
    id: "automations.duplicate",
    method: "post",
    path: `${base}/automations/{id}/duplicate`,
    tag: "automations",
    summary: "Copy a rule (switched off, owned by the caller)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 201, schema: AutomationResult },
    errors: [...automationErrors],
    emits: ["automation.created", "approval.requested"],
  }),
  "automations.runs": defineRoute({
    ...core,
    id: "automations.runs",
    method: "get",
    path: `${base}/automations/{id}/runs`,
    tag: "automations",
    summary: "A rule's firings, newest first (History)",
    access: "member",
    params: CompanyItemParams,
    query: AutomationRunsQuery,
    response: { status: 200, schema: AutomationRunList },
    errors: [],
    emits: [],
  }),

  "seats.routines": defineRoute({
    ...core,
    id: "seats.routines",
    method: "get",
    path: `${base}/seats/{id}/routines`,
    tag: "responsibilities",
    summary: "A seat's recurring work with its controls, next run and last occurrence",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: RoutineList },
    errors: [],
    emits: [],
  }),
  "seats.routinesCreate": defineRoute({
    ...core,
    id: "seats.routinesCreate",
    method: "post",
    path: `${base}/seats/{id}/routines`,
    tag: "responsibilities",
    summary: "Add a routine to a seat (an agent's is a proposal: propose_routine)",
    access: "member",
    params: CompanyItemParams,
    body: RoutineCreate,
    idempotency: "optional",
    response: { status: 201, schema: RoutineResult },
    errors: ["schedule_invalid", "seat_closed", "policy_denied"],
    emits: ["responsibility.created", "recurring_work.created", "approval.requested"],
  }),
  "tasks.repeat": defineRoute({
    ...core,
    id: "tasks.repeat",
    method: "post",
    path: `${base}/tasks/{id}/repeat`,
    tag: "responsibilities",
    summary: "Repeat this task: recurring work on a seat's responsibility, the task as template",
    access: "manager",
    params: CompanyItemParams,
    body: TaskRepeat,
    idempotency: "optional",
    response: { status: 201, schema: RoutineView },
    errors: ["schedule_invalid", "seat_closed"],
    emits: ["responsibility.created", "recurring_work.created"],
  }),
  "recurringWork.pause": defineRoute({
    ...core,
    id: "recurringWork.pause",
    method: "post",
    path: `${base}/recurring-work/{id}/pause`,
    tag: "responsibilities",
    summary: "Pause recurring work (no occurrence until resumed)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RoutineView },
    errors: ["conflict"],
    emits: ["recurring.paused", "recurring_work.updated"],
  }),
  "recurringWork.resume": defineRoute({
    ...core,
    id: "recurringWork.resume",
    method: "post",
    path: `${base}/recurring-work/{id}/resume`,
    tag: "responsibilities",
    summary: "Resume recurring work from the next occurrence after now",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RoutineView },
    errors: ["conflict", "schedule_invalid"],
    emits: ["recurring.resumed", "recurring_work.updated"],
  }),
  "recurringWork.skip": defineRoute({
    ...core,
    id: "recurringWork.skip",
    method: "post",
    path: `${base}/recurring-work/{id}/skip`,
    tag: "responsibilities",
    summary: "Skip the next occurrence (no task); the schedule moves past it",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RoutineView },
    errors: ["conflict"],
    emits: ["recurring.skipped", "recurring_work.updated"],
  }),
  "recurringWork.takeOver": defineRoute({
    ...core,
    id: "recurringWork.takeOver",
    method: "post",
    path: `${base}/recurring-work/{id}/take-over`,
    tag: "responsibilities",
    summary: "Take the work over: the open occurrence, or all future ones (or hand it back)",
    access: "manager",
    params: CompanyItemParams,
    body: RecurringTakeOver,
    response: { status: 200, schema: RoutineView },
    errors: ["conflict", "seat_closed"],
    emits: ["recurring.taken_over", "recurring_work.updated", "task.assigned", "task.unassigned"],
  }),
  "recurringWork.history": defineRoute({
    ...core,
    id: "recurringWork.history",
    method: "get",
    path: `${base}/recurring-work/{id}/history`,
    tag: "responsibilities",
    summary: "A routine's occurrences and controls, newest first",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: RecurringHistory },
    errors: [],
    emits: [],
  }),
} as const;
